import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, UserPlus, Activity, Euro, TrendingUp, TrendingDown, Instagram, Sparkles, AlertTriangle, Search } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { MetricSkeleton } from "@/components/Skeletons";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { formatNumber, formatDate } from "@/lib/format";
import { useT } from "@/i18n";

export function Admin() {
  const { t } = useT();
  const [q, setQ] = useState("");
  const { data: stats, isLoading } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, retry: false });
  const { data: users } = useQuery({ queryKey: ["admin-users", q], queryFn: async () => (await api.get(`/admin/users?q=${encodeURIComponent(q)}`)).data, retry: false });

  const cards = stats ? [
    { icon: Users, label: t("admin.totalUsers"), value: formatNumber(stats.total_users) },
    { icon: UserPlus, label: t("admin.newUsers"), value: formatNumber(stats.new_users_7d) },
    { icon: Activity, label: t("admin.activeUsers"), value: formatNumber(stats.active_users) },
    { icon: Euro, label: t("admin.mrr"), value: `€${formatNumber(stats.mrr)}` },
    { icon: Users, label: t("admin.freeUsers"), value: formatNumber(stats.free_users) },
    { icon: Sparkles, label: t("admin.proUsers"), value: formatNumber(stats.pro_users) },
    { icon: Users, label: t("admin.agencyUsers"), value: formatNumber(stats.agency_users) },
    { icon: TrendingUp, label: t("admin.conversion"), value: `${stats.conversion_rate}%` },
    { icon: TrendingDown, label: t("admin.churn"), value: `${stats.churn_rate}%` },
    { icon: Instagram, label: t("admin.igConnected"), value: formatNumber(stats.instagram_connected) },
    { icon: Sparkles, label: t("admin.aiUsage"), value: formatNumber(stats.ai_calls) },
    { icon: AlertTriangle, label: t("admin.apiErrors"), value: formatNumber(stats.api_errors) },
  ] : [];

  const planBadge = { free: "secondary", pro: "default", agency: "default" };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("admin.title")}</h1>

        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{[0,1,2,3].map(i => <MetricSkeleton key={i} />)}</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-stats">
            {cards.map((c) => (
              <div key={c.label} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-1.5 text-muted-foreground"><c.icon className="h-4 w-4" /><span className="text-xs font-mono uppercase tracking-wide">{c.label}</span></div>
                <div className="mt-2 font-display text-2xl font-bold">{c.value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-display text-lg font-semibold">{t("admin.users")}</h2>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.searchUsers")} data-testid="admin-search" className="rounded-xl pl-9" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("account.name")}</TableHead><TableHead>{t("account.email")}</TableHead>
                  <TableHead>{t("account.plan")}</TableHead><TableHead>Registrato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="admin-users-table">
                {users?.users?.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant={planBadge[u.plan] || "secondary"} className="capitalize">{u.plan}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
